import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import useToggle from 'shared/hooks/use-toggle';
import { toast } from 'react-hot-toast';

const base = endpoints.serviceCatalog.base;

const unwrapRecords = (axiosResponse) => {
  const records = axiosResponse?.data?.data;
  if (!records || typeof records !== 'object') {
    return { rows: [], total: 0, page: 0, size: 0 };
  }
  return {
    rows: Array.isArray(records.data) ? records.data : [],
    total: Number(records.records) || 0,
    page: records.page ?? 0,
    size: records.size ?? 0,
  };
};

const useServiceCatalogService = () => {
  const [services, setServices] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  const fetchServices = useCallback(
    async (page, size, status = null, search = null) => {
      toggleLoading();
      try {
        const params = new URLSearchParams();
        params.set('page', String(page ?? 0));
        params.set('size', String(size ?? 20));
        if (status != null && String(status).trim() !== '') {
          params.set('status', String(status).trim());
        }
        if (search != null && String(search).trim() !== '') {
          params.set('search', String(search).trim());
        }
        const response = await axiosInstance.get(`${base}?${params.toString()}`);
        const { rows, total } = unwrapRecords(response);
        setServices(rows);
        setTotalRecords(total);
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Failed to load services.';
        toast.error(typeof msg === 'string' ? msg : 'Failed to load services.');
        console.error('Fetch services error:', error);
        setServices([]);
        setTotalRecords(0);
      } finally {
        toggleLoading();
      }
    },
    [toggleLoading]
  );

  const createService = useCallback(async (payload) => {
    const response = await axiosInstance.post(base, payload);
    return response.data?.data ?? response.data;
  }, []);

  const updateService = useCallback(async (payload) => {
    const response = await axiosInstance.put(base, payload);
    return response.data?.data ?? response.data;
  }, []);

  const deleteService = useCallback(async (id) => {
    const response = await axiosInstance.delete(`${base}/${encodeURIComponent(id)}`);
    return response.data?.data ?? response.data;
  }, []);

  return {
    services,
    totalRecords,
    isLoading,
    fetchServices,
    createService,
    updateService,
    deleteService,
  };
};

export default useServiceCatalogService;
